import { type GridColDef, GridActionsCellItem, type GridSortModel } from '@mui/x-data-grid-pro';
import { Grid2, Box, Link, LinearProgress, linearProgressClasses, styled, Typography, FormControl, TextField, FormHelperText, Select, MenuItem, Stack, Chip, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import lockV2Icon from '../../assets/Lock-V2.svg';
import unlock02Icon from '../../assets/Unlock-02.svg';

import { toTitleCase,numericOnlyKeyDown } from '../../utils/HelperFunction';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { getStatusStyle } from '../../utils/SPLotGridConfig';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import { createRenderColumnHeader } from '../common/GridHeaderUtils';
import type { Theme } from '@mui/material/styles';
import { applyDashValueFormatter, DASH_PLACEHOLDER, formatMissingText } from '../../utils/missingValue';

// Wrapper component for Select with dynamic dropdown positioning
const GridSelect = ({
    value,
    onChange,
    renderValue,
    children,
    sx,
    className,
    disabled
}: {
    value: any;
    onChange: (e: any) => void;
    renderValue: (selected: any) => React.ReactNode;
    children: React.ReactNode;
    sx?: any;
    className?: string;
    disabled?: boolean;
}) => {
    const { menuPosition, calculatePosition } = useDropdownPosition(300);
    return (
        <Select
            labelId="grid-select-label"
            displayEmpty
            value={value}
            disabled={disabled}
            className={className}
            onOpen={calculatePosition}
            MenuProps={{
                anchorOrigin: menuPosition.anchorOrigin,
                transformOrigin: menuPosition.transformOrigin,
                PaperProps: { style: { maxHeight: 300, marginTop: menuPosition.anchorOrigin.vertical === "top" ? -8 : 8 } }
            }}
            sx={sx}
            renderValue={renderValue}
            onChange={onChange}
        >
            {children}
        </Select>
    );
};

const BorderLinearProgress = styled(LinearProgress)<{ barcolor?: string }>(({ theme, barcolor }) => {
    return ({
        width: '100%',
        height: 20,
        borderRadius: 10,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey[200],
            ...theme.applyStyles('dark', {
                backgroundColor: theme.palette.grey[800],
            }),
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 10,
            backgroundColor: barcolor || '#2B7FFF',
            ...theme.applyStyles('dark', {
                backgroundColor: barcolor || '#2B7FFF',
            }),
        },
    })
});

const getProgressColor = (percentage: number): string => {
    if (!percentage || percentage <= 0) return 'transparent';
    if (percentage >= 100) return '#00AC4F';
    if (percentage >= 67) return '#2B7FFF';
    if (percentage >= 34) return '#EBC220';
    return '#D8010B';
};

// Common render cell for text fields with titlecase
const renderTextCell = (params: any, classes: any) => {
    const displayValue = formatMissingText(params.value);
    return (
        <Typography className={classes.gridRowText} sx={displayValue === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
            {displayValue === DASH_PLACEHOLDER ? DASH_PLACEHOLDER : toTitleCase(String(params.value))}
        </Typography>
    );
};

// const renderReadOnlyCell = (params: any, classes: any) => {
//     return (
//         <Typography className={classes.gridRowText}>
//             {params.value !== null && params.value !== undefined && params.value !== '' ? String(params.value) : ''}
//         </Typography>
//     );
// };

// Renders value for parent rows only — blank for harvest child rows
const renderParentOnlyCell = (params: any, classes: any, titleCase = false) => {
    if (params.row._rowType === 'child') return <Typography className={classes.gridRowText} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>;
    const val = formatMissingText(params.value);
    return <Typography className={classes.gridRowText}>{titleCase ? toTitleCase(val) : val}</Typography>;
};

const renderEditableNumberField = (
    params: any,
    fieldName: string,
    classes: any,
    childEditable: boolean,
    parentEditable: boolean,
    onRowUpdate?: (field: string, value: any, rowId: string) => void,
    disabled?: boolean,

) => {
    // If child row, show as read-only and keep empty values blank
    if ((params.row._rowType === 'child' && !childEditable) || (params.row._rowType === 'parent' && !parentEditable)) {
        return (
            <Typography className={classes.gridRowText} sx={{ fontSize: formatMissingText(params.value) === '—' ? 16 : 'inherit', fontWeight: formatMissingText(params.value) === '—' ? 600 : 'inherit' }}>
                {formatMissingText(params.value)}
            </Typography>
        );
    }

    // If field is not editable (disabled), show as plain text
    if (disabled) {
        return (
            <Typography className={classes.gridRowText} sx={{ fontSize: formatMissingText(params.value) === '—' ? 16 : 'inherit', fontWeight: formatMissingText(params.value) === '—' ? 600 : 'inherit' }}>
                {formatMissingText(params.value)}
            </Typography>
        );
    }

    return (
        <>
            {((params.row._rowType === 'child' && childEditable) || (params.row._rowType === 'parent' && parentEditable)) && (
                <FormControl size="small" fullWidth>
                    <TextField
                        variant="outlined"
                        type="number"
                        disabled={false}
                        value={params.value}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || (+value >= 0 && +value <= 1000)) {
                                onRowUpdate?.(
                                    fieldName,
                                    value,
                                    params
                                );
                            }
                        }}
                       onKeyDown={numericOnlyKeyDown}
                        slotProps={{ htmlInput: { min: 0, max: 1000 } }}
                        sx={(theme) => ({
                            backgroundColor: '#EFF6FF',
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                            ...MtComStyleObj(theme).textBoxValueStyle,
                        })}
                    />
                    <FormHelperText
                        sx={{ ml: 0 }}
                        className={classes.errorText}
                    />
                </FormControl>
            )}
        </>
    );
};

export function getTrialsGridCols(
    classes: any,
    trailStyles: any,
    downloadTrialMaterials?: (trialBid: string, totalMaterials?: number) => void,
    onMarkTrialCompleteClick?: (trialBid: string, trialStatus?: string) => void,
    isArchived?: boolean,
    sortModel: GridSortModel = [],
    canMarkComplete?: boolean,
    onOpenTrialDetails?: (row: any) => void
): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);
    let columns: GridColDef[] = [
        {
            field: 'trial_bid', headerName: 'Trial ID', minWidth: 125, flex: 1, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`,
            renderHeader: renderHeader,
            renderCell: (params: any) => (
                <Box sx={{ width: "100%", display: "flex" }}>
                    <Link
                        className={classes.labelNoto14Bold400}
                        component="button"
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenTrialDetails?.(params?.row);
                        }}
                        sx={{
                        cursor: 'pointer', color: '#0F57DE', textDecoration: "underline", "&:hover, &:visited": {
                            color: "#094BC7",
                            textDecoration: "underline"
                        },
                        background: 'none',
                        border: 'none',
                        p: 0,
                        font: 'inherit',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                    }}
                    >
                        {params?.row?.trial_bid}
                    </Link>
                </Box>
            ),
        },
        { field: 'crop', renderHeader: renderHeader, headerName: 'Crop', minWidth: 100, flex: 1, type: 'singleSelect', cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`, renderCell: (params) => renderTextCell(params, classes) },
        {
            field: 'trial_status', renderHeader: renderHeader, headerName: 'Trial Status', minWidth: 120, flex: 1, cellClassName: trailStyles.gridCellWithInput,
            renderCell: (params: any) => (
                params?.value ? <Chip className={classes.labelNoto14Bold400} label={toTitleCase(params?.value ?? "")} sx={(theme: Theme,) => ({
                    ...getStatusStyle(theme, params?.value?.toLowerCase()),
                    borderRadius: '8px',

                })} /> : <Typography className={classes.gridRowText} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>
            )
        },
        { field: 'sector', renderHeader: renderHeader, headerName: 'Greenhouse Location', minWidth: 100, flex: 1, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`, renderCell: (params) => renderTextCell(params, classes) },
        {
            field: 'local_code', renderHeader: renderHeader, headerName: 'Description', minWidth: 120, flex: 1, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`,
            renderCell: (params: any) => (
                <Typography className={classes.gridRowText}>
                    {formatMissingText(params?.row?.local_code)}
                </Typography>
            )
        },

        {
            field: 'start_date', renderHeader: renderHeader, headerName: 'Submission Date', minWidth: 100, flex: 1, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`,
            renderCell: (params: any) => {
                const displayValue = params?.row?.start_date ? new Date(params?.row?.start_date).toLocaleDateString('en-US') : DASH_PLACEHOLDER;
                return (
                    <Typography className={classes.gridRowText} sx={displayValue === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                        {displayValue}
                    </Typography>
                );
            }
        },
        {
            field: 'matid_completion', renderHeader: renderHeader, headerName: 'Mat ID Completion %', minWidth: 200, flex: 2,
            renderCell: (params: any) => {
                const parsedValue = Number(params?.row?.matid_completion);
                const rawValue = Number.isFinite(parsedValue) ? parsedValue : 0;
                const roundedValue = Math.round(rawValue * 10) / 10;
                const cappedValue = roundedValue > 100 ? 100 : roundedValue;
                const color = getProgressColor(roundedValue);
                return (
                    <Stack direction="column" sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{ color: 'black' }}
                            >{`${roundedValue.toFixed(1)}%`}</Typography>
                        </Box>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <BorderLinearProgress variant="determinate" value={cappedValue} barcolor={color} />
                            <Typography
                                variant="body2"
                                className={`${trailStyles.CenterAlignText}`}
                                sx={{ paddingLeft: '5px' }}
                            >{`${Math.round(params?.row?.completed_materials)}/${params?.row?.total_materials}`}</Typography>
                        </Stack>
                    </Stack>
                );
            },
        },
        {
            field: 'last_date', renderHeader: renderHeader, headerName: 'Last Updated On', minWidth: 150, flex: 1, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`,
            renderCell: (params: any) => {
                const displayValue = params?.row?.last_date ? new Date(params?.row?.last_date).toLocaleDateString('en-US') : DASH_PLACEHOLDER;
                return (
                    <Typography className={classes.gridRowText} sx={displayValue === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                        {displayValue}
                    </Typography>
                );
            }
        },
        {
            field: '', headerName: 'Actions', width: 120, type: "actions",  sortable:false, cellClassName: `${classes.gridRowText} ${trailStyles.CenterAlignText}`,
            getActions: (params: any) => {
                const trialStatus = String(params?.row?.trial_status ?? '').trim().toLowerCase();
                const showLockIcon = !isArchived && !!canMarkComplete;
                const isInProgress = trialStatus === 'in progress';
                const isReadyForHarvest = trialStatus === 'ready for harvest';
                const showOpenLock = isInProgress || isReadyForHarvest;
                const normalizedStatus = trialStatus.replace(/[_-]+/g, ' ').trim();
                const closedStatusTooltip =
                    normalizedStatus === 'completed'
                        ? 'Trial is in completed status'
                        : normalizedStatus === 'manually completed'
                            ? 'Trial is in manually completed status'
                            : normalizedStatus === 'ready for harvest'
                                ? 'Trial is in ready for harvest status'
                                : `Trial is in ${toTitleCase(normalizedStatus || 'completed')} status`;

                return [
                    ...(isArchived ? [
                        <Tooltip key="download-archived" title="Download Trial details" arrow placement="top">
                            <GridActionsCellItem
                                icon={<FileDownloadOutlinedIcon />}
                                label="Download"
                                disableFocusRipple disableRipple
                                className="textPrimary"
                                color="inherit"
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).gridIconHoverStyle, boxShadow: "none"
                                })}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    downloadTrialMaterials?.(params.row.trial_bid, params.row.total_materials);
                                }}
                            />
                        </Tooltip>
                    ] : [
                        <Tooltip key="download" title="Download Trial details" arrow placement="top">
                            <GridActionsCellItem
                                icon={<FileDownloadOutlinedIcon />}
                                label="Download"
                                disableFocusRipple disableRipple
                                className="textPrimary"
                                color="inherit"

                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).gridIconHoverStyle, boxShadow: "none"
                                })}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    downloadTrialMaterials?.(params.row.trial_bid, params.row.total_materials);
                                }}
                            />
                        </Tooltip>
                    ]),
                    ...(showLockIcon ? [
                        showOpenLock ? (
                            <Tooltip key="lock-open-tooltip" title="Mark as complete" arrow placement="top">
                                <GridActionsCellItem
                                    key="lock-open"
                                    icon={<Box component="img" src={unlock02Icon} alt="unlock" sx={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }} />}
                                    label="Mark as complete"
                                    disableFocusRipple
                                    disableRipple
                                    className="textPrimary"
                                    color="inherit"
                                    sx={(theme) => ({
                                        ...MtComStyleObj(theme).gridIconHoverStyle, boxShadow: "none"
                                    })}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onMarkTrialCompleteClick?.(params.row.trial_bid, params?.row?.trial_status);
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip key="lock-closed-tooltip" title={closedStatusTooltip} arrow placement="top">
                                <span>
                                    <GridActionsCellItem
                                        key="lock-closed"
                                        icon={<Box component="img" src={lockV2Icon} alt="lock" sx={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }} />}
                                        label={closedStatusTooltip}
                                        disableFocusRipple
                                        disableRipple
                                        className="textPrimary"
                                        color="inherit"
                                        sx={(theme) => ({
                                            ...MtComStyleObj(theme).gridIconHoverStyle, boxShadow: "none", pointerEvents: 'none'
                                        })}
                                        onClick={(event) => { event.stopPropagation(); }}
                                    />
                                </span>
                            </Tooltip>
                        )
                    ] : [])
                ];
            }
        }
    ];

    return applyDashValueFormatter(columns);
}


export function getTrialMaterialGridCols(
    classes: any,
    trailClasses: any,
    _: any[] = [],
    remarks: any[] = [],
    onRowUpdate?: (field: string, value: any, rowId: string) => void,
    sortModel: GridSortModel = [],
    isArchived: boolean = false,
    roleSelected: string = '',
    priorities: { id: string; value: string }[] = [],
    onToggleAllExpand?: () => void,
    areAllExpanded: boolean = false,
    hasExpandableRows: boolean = false,
): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);
    const canEditMaterialFields = ["S2S-Lead", "Admin"].includes(roleSelected);
    const allColumns: GridColDef[] = [
        {
            field: '__expand__',
            headerName: '',
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: () => {
                if (!hasExpandableRows) return <></>;

                const tooltipTitle = areAllExpanded ? 'Collapse all' : 'Expand all';
                return (
                    <Tooltip title={tooltipTitle} arrow placement="top">
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                                p: 0.5,
                                borderRadius: '4px',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                },
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onToggleAllExpand?.();
                            }}
                        >
                            {areAllExpanded
                                ? <ExpandMoreIcon fontSize="small" sx={{ color: 'black' }} />
                                : <ChevronRightIcon fontSize="small" sx={{ color: 'black' }} />}
                        </Box>
                    </Tooltip>
                );
            },
            renderCell: (params: any) => {
                if (params.row._rowType === 'child') {
                    return <Box sx={{ pl: 2 }} />;
                }

                if (!params.row._hasChildren) {
                    return <Box />;
                }

                const isExpanded = !!params.row._isExpanded;
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: '4px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                            '& svg': {
                                transition: 'transform 0.3s ease-in-out',
                                transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
                            }
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            params.row._onToggleExpand?.(params.row._rowUid);
                        }}
                    >
                        {isExpanded ? <ExpandMoreIcon fontSize="small" sx={{ color: 'black' }} /> : <ChevronRightIcon fontSize="small" sx={{ color: 'black' }} />}
                    </Box>
                );
            }
        },
        {
            field: 'mat_id',
            renderHeader: renderHeader,
            headerName: 'Material ID',
            minWidth: 130,
            flex: 1,
            cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => (
                <Typography className={classes.gridRowText} sx={{ pl: params.row._rowType === 'child' ? 1.5 : 0, color: params.row._isNoDataRow ? '#6D6D6D' : undefined }}>
                    {params.row._isNoDataRow
                        ? 'No data available'
                        : params.row._rowType === 'child'
                            ? ''
                            : (params.value ?? '')}
                </Typography>
            )
        },
        {
            field: 'harvest_count', renderHeader: renderHeader, headerName: 'Harvests', minWidth: 120, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params) => {
                if (params.row._rowType === 'child') {
                    return (
                        <Typography className={classes.gridRowText}>
                            {formatMissingText(params.row.harvest_name)}
                        </Typography>
                    );
                }
                return (
                    <Typography className={classes.gridRowText}>
                        {params.value ?? 0}
                    </Typography>
                );
            }
        },
        {
            field: 'splot_id', renderHeader: renderHeader, headerName: 'SPLot ID', minWidth: 140, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            // Harvest rows carry their own splot_id, so this column renders on child rows too.
            renderCell: (params: any) => {
                const val = formatMissingText(params.row?.splot_id ?? params.row?.sp_lot_id ?? '');
                return <Typography className={classes.gridRowText} sx={{ fontSize: val === '—' ? 16 : 'inherit', fontWeight: val === '—' ? 600 : 'inherit' }}>{val}</Typography>;
            }
        },
        { field: 'pollination_id', renderHeader: renderHeader, headerName: 'Pollination ID', minWidth: 140, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'barcode', renderHeader: renderHeader, headerName: 'Barcode', minWidth: 130, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'admin_code', renderHeader: renderHeader, headerName: 'Admin Code', minWidth: 130, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'sector', renderHeader: renderHeader, headerName: 'Greenhouse Location', minWidth: 110, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'pollination_type', renderHeader: renderHeader, headerName: 'Pollination Type', minWidth: 150, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes, true) },
        { field: 'type_segment', renderHeader: renderHeader, headerName: 'Type/Segment', minWidth: 140, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes, true) },
        // { field: 'PSSST', renderHeader: renderHeader, headerName: 'PSSST', minWidth: 110, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'hrvmd', renderHeader: renderHeader, headerName: 'HRVMD', minWidth: 110, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        { field: 'special_requisites', renderHeader: renderHeader, headerName: 'Special Requisites', minWidth: 160, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes) },
        {
            field: 'processing_priority', renderHeader: renderHeader, headerName: 'Processing Priority', minWidth: 160, flex: 1, cellClassName: trailClasses.gridCellWithInput,
            renderCell: (params: any) => {
                if (params.row._rowType === 'child') {
                    return <Typography className={classes.gridRowText} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>;
                }
                const isEditable = !isArchived && params.row?.material_status === "Ready For Harvest" && ["S2S-Lead", "Admin"].includes(roleSelected);
                const currentValue = params.value ?? '';
                
                if (!isEditable) {
                    const match = priorities.find((p) => p.id === currentValue);
                    const displayValue = match?.value || formatMissingText(currentValue);
                    return (
                        <Typography className={classes.gridRowText} sx={displayValue === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                            {displayValue === DASH_PLACEHOLDER ? DASH_PLACEHOLDER : toTitleCase(String(displayValue))}
                        </Typography>
                    );
                }
                
                return (
                    <FormControl size="small" fullWidth>
                        <GridSelect
                            value={currentValue}
                            disabled={false}
                            className={classes.labelNoto16Bold400}
                            sx={(theme: any) => ({
                                ...MtComStyleObj(theme).fieldFocusHoverStyle,
                                backgroundColor: "#EFF6FF",
                            })}
                            renderValue={(selected) => {
                                if (!selected) return <span className={classes.labelNoto16Bold400}>Select Priority</span>;
                                const match = priorities.find((p) => p.id === selected);
                                return match?.value || toTitleCase(String(selected));
                            }}
                            onChange={(e) => {
                                if (onRowUpdate) {
                                    onRowUpdate('processing_priority', e.target.value, params);
                                }
                            }}
                        >
                            {priorities.map((p) => <MenuItem key={p.id} value={p.id}>{p.value}</MenuItem>)}
                        </GridSelect>
                        <FormHelperText sx={{ ml: 0 }} className={classes.errorText}></FormHelperText>
                    </FormControl>
                );
            },
        },
        {
            field: 'plants_to_harvest', renderHeader: renderHeader, headerName: 'Subplot ID', minWidth: 150, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params) => renderParentOnlyCell(params, classes)
        },
        { field: 'material_generation', renderHeader: renderHeader, headerName: 'Generation Code', minWidth: 160, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => renderParentOnlyCell(params, classes, true) },
        {
            field: 'material_stage_code', renderHeader: renderHeader, headerName: 'Stage Code', minWidth: 160, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params) => {
                if (params.row._rowType === 'child') {
                    return <Typography className={classes.gridRowText}></Typography>;
                }
                return renderParentOnlyCell(params, classes);
            }
        },
        {
            field: 'required_grams', renderHeader: renderHeader, headerName: 'Required Seed Weight (g)', minWidth: 220, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.gridCellWithInput}`,
            renderCell: (params: any) => renderEditableNumberField(params, 'required_grams', classes, false, true, onRowUpdate, isArchived || !["Ready For Harvest"].includes(params.row?.material_status) || !canEditMaterialFields),
        },
        { field: 'required_seed_count', renderHeader: renderHeader, headerName: 'Required Seed Count', minWidth: 170, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.gridCellWithInput}`, renderCell: (params: any) => renderEditableNumberField(params, 'required_seed_count', classes, false, true, onRowUpdate, isArchived || !["Ready For Harvest"].includes(params.row?.material_status) || !canEditMaterialFields), },
        { field: 'required_fruit', renderHeader: renderHeader, headerName: 'Required Fruit Count', minWidth: 170, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.gridCellWithInput}`, renderCell: (params: any) => renderEditableNumberField(params, 'required_fruit', classes, false, true, onRowUpdate, isArchived || !["Ready For Harvest"].includes(params.row?.material_status) || !canEditMaterialFields), },
        {
            field: 'seed_weight_g', renderHeader: renderHeader, headerName: 'Seed Weight (g)', minWidth: 150, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => {
                const displayValue = formatMissingText(params.value);
                return (
                    <Typography className={classes.gridRowText} sx={{ fontSize: displayValue === '—' ? 16 : 'inherit', fontWeight: displayValue === '—' ? 600 : 'inherit' }}>
                        {displayValue}
                        {/* {params?.row?._rowType === 'child' ?  params?.row?.seed_weight :  params?.row?.seed_weight_g  } */}
                    </Typography>
                );
            }
        },
        {
            field: 'seed_count', renderHeader: renderHeader, headerName: 'Seed Count', minWidth: 120, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`, renderCell: (params) => {
                const displayValue = formatMissingText(params.value);
                return (
                    <Typography className={classes.gridRowText} sx={{ fontSize: displayValue === '—' ? 16 : 'inherit', fontWeight: displayValue === '—' ? 600 : 'inherit' }}>
                        {displayValue}
                    </Typography>
                );

            }
        },
        {
            field: 'fruit_count', renderHeader: renderHeader, headerName: 'Fruit Count', minWidth: 120, flex: 1, cellClassName: trailClasses.gridCellWithInput,
            renderCell: (params: any) => renderEditableNumberField(params, 'fruit_count', classes, true, false, onRowUpdate, isArchived || !["Ready For Harvest", "Processing", "Harvested"].includes(params.row?.harvest_status) || !canEditMaterialFields),
        },

        {
            field: 'fruit_weight_g', renderHeader: renderHeader, headerName: 'Fruit Weight (g)', minWidth: 140, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.gridCellWithInput}`,
            renderCell: (params: any) => renderEditableNumberField(params, 'fruit_weight_g', classes, true, false, onRowUpdate, isArchived || !["Ready For Harvest", "Processing", "Harvested"].includes(params.row?.harvest_status) || !canEditMaterialFields),
        },
        {
            field: 'bags', renderHeader: renderHeader, headerName: 'Bags', minWidth: 90, flex: 1, cellClassName: trailClasses.gridCellWithInput,
            renderCell: (params: any) => renderEditableNumberField(params, 'bags', classes, true, false, onRowUpdate, isArchived || !["Ready For Harvest", "Processing", "Harvested"].includes(params.row?.harvest_status) || !canEditMaterialFields),
        },




        {
            field: 'all_registered_containers', renderHeader: renderHeader, headerName: 'Registered Containers', minWidth: 200, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => {
                const row = params.row ?? {};
                // if (row._rowType === 'child') {
                //     // Harvest level: show the harvest's container_alt_id
                //     const val = params.value;
                //     if (Array.isArray(val)) {
                //         const ids = val.map((c: any) => c?.container_alt_id ?? c).filter(Boolean);
                //         return <Typography className={classes.gridRowText}>{ids.join(', ')}</Typography>;
                //     }
                //     //return <Typography className={classes.gridRowText}>{val != null && val !== '' ? String(val) : ''}</Typography>;
                // }

                const containers = row.all_registered_containers ? row.all_registered_containers.split(",") : []
                if (row._rowType === 'parent' && Array.isArray(containers) && containers.length > 0) {
                    const ids = containers.map((c: any) => c?.container_alt_id ?? c).filter(Boolean);
                    return <Typography title={ids.join(', ')} className={classes.gridRowText}>{ids.join(', ')}</Typography>;
                }
                else if (row._rowType === 'child') {
                    const displayValue = row.registered_containers ? String(row.registered_containers) : '—';
                    return <Typography title={row.registered_containers} className={classes.gridRowText} sx={{ fontSize: displayValue === '—' ? 16 : 'inherit', fontWeight: displayValue === '—' ? 600 : 'inherit' }}>{displayValue}</Typography>;
                }
                else {
                    return <Typography className={classes.gridRowText} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>;
                }


                //return <Typography className={classes.gridRowText}>{params.value != null && params.value !== '' ? String(params.value) : ''}</Typography>;
            },
        },
        {
            field: 'goal_achieved_pct', renderHeader: renderHeader, headerName: 'Goal Achieved %', minWidth: 260, flex: 1,
            renderCell: (params: any) => {
                if (params.row._rowType === 'child') {
                    return (
                        <Typography className={classes.gridRowText}></Typography>
                    );
                }

                const percentage = params.value || 0;
                const rawValue = params.value;
                const cappedValue = rawValue > 100 ? 100 : rawValue;
                const color = getProgressColor(rawValue);

                return (
                    <Stack direction="column" sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{ color: 'black' }}
                            >{`${percentage}%`}</Typography>
                        </Box>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <BorderLinearProgress variant="determinate" value={cappedValue} barcolor={color} />
                        </Stack>
                    </Stack>
                );
            },
        },
        {
            field: 'current_status', renderHeader: renderHeader, headerName: 'Status', flex: 1, minWidth: 150, type: 'singleSelect', cellClassName: trailClasses.gridCellWithInput,
            renderCell: (params: any) => {
                if (params.row._rowType === 'child') {
                    const childHarvestStatus = params.row?.harvest_status || '';
                    return (
                        <Typography className={classes.gridRowText}>
                            {childHarvestStatus}
                            {/* {childHarvestStatus ? toTitleCase(String(childHarvestStatus)) : ''} */}
                        </Typography>
                    );
                }
                else {
                    const materialLevelStatus = params.row?.material_status || '';
                    return (
                        <Typography title={materialLevelStatus} className={classes.gridRowText}>
                            {materialLevelStatus}
                        </Typography>
                    );

                }
            },
        },
        {
            field: 'remarks', renderHeader: renderHeader, headerName: 'Remarks', flex: 1, minWidth: 160, cellClassName: trailClasses.gridCellWithInput,
            renderCell: (params: any) => {
                const rawRemarkValue = params.row.remark_id ?? params.row.material_remark ?? params?.value ?? '';
                const remarkId = typeof rawRemarkValue === 'number'
                    ? rawRemarkValue
                    : (remarks.find((r: any) => r.remark_name === rawRemarkValue)?.remark_id ?? rawRemarkValue);

                // Child rows: display remark name as plain text
                if (params.row._rowType === 'child') {
                    const remarkName = typeof remarkId === 'number'
                        ? (remarks.find((r: any) => r.remark_id === remarkId)?.remark_name ?? '')
                        : (typeof rawRemarkValue === 'string' ? rawRemarkValue : '');
                    return (
                        <Typography className={classes.labelNoto14Bold400} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {toTitleCase(remarkName)}
                        </Typography>
                    );
                }

                const isEditable = !isArchived && canEditMaterialFields;
                
                if (!isEditable) {
                    const remarkName = typeof remarkId === 'number'
                        ? (remarks.find((r: any) => r.remark_id === remarkId)?.remark_name ?? '')
                        : (typeof rawRemarkValue === 'string' ? rawRemarkValue : '');
                    return (
                        <Typography className={classes.labelNoto14Bold400} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {toTitleCase(remarkName)}
                        </Typography>
                    );
                }

                return (
                    <FormControl size="small" fullWidth>
                        <Grid2>

                            <Select
                                value={remarkId}
                                disabled={false}
                                displayEmpty
                                className={classes.labelNoto16Bold400}
                                sx={(theme: any) => ({
                                    width: "100%",
                                    ...MtComStyleObj(theme).fieldFocusHoverStyle,
                                    backgroundColor: "#EFF6FF",
                                })}
                                renderValue={(selected) => {
                                    if (!selected) return <span className={classes.labelNoto14Bold400}>Select Remark</span>;
                                    const selectedRemark = remarks.find((r: any) => r.remark_id === selected);
                                    return toTitleCase(selectedRemark?.remark_name || String(selected));
                                }}
                                onChange={(e) => {
                                    const selectedRemark = remarks.find((r: any) => r.remark_id === e.target.value) ?? "";
                                    if (onRowUpdate) onRowUpdate('remarks', selectedRemark.remark_id, params);
                                }}
                            >
                                {remarks.filter((remark: any) => remark.remark_id !== 8).map((remark: any) => <MenuItem key={remark.remark_id} value={remark.remark_id}
                                    onClick={() => {
                                        if (onRowUpdate) onRowUpdate('remarks', null, params);
                                    }}

                                >{toTitleCase(remark.remark_name)}</MenuItem>)}

                            </Select>
                        </Grid2>

                        <FormHelperText sx={{ ml: 0 }} className={classes.errorText}></FormHelperText>
                    </FormControl>
                );
            },
        }

    ];

    const requestedOrder = [
        'mat_id',
        'harvest_count',
        'splot_id',
        'pollination_id',
        'barcode',
        'admin_code',
        'sector',
        'pollination_type',
        'type_segment',
        'hrvmd',
        'special_requisites',
        'processing_priority',
        'plants_to_harvest',
        'material_generation',
        'material_stage_code',
        'required_grams',
        'required_seed_count',
        'required_fruit',
        'seed_weight_g',
        'seed_count',
        'fruit_count',
        'fruit_weight_g',
        'bags',
        'all_registered_containers',
        'goal_achieved_pct',
        'current_status',
        'remarks',
    ];

    const aliasColumns: GridColDef[] = [
        {
            field: 'material_status', renderHeader: renderHeader, headerName: 'Material Status', minWidth: 150, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => {
                const displayValue = params.row?._rowType === 'child' ? DASH_PLACEHOLDER : formatMissingText(params.row?.material_status);
                return (
                    <Typography className={classes.gridRowText} sx={displayValue === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                        {displayValue}
                    </Typography>
                );
            },
        },
        {
            field: 'required_seed_weight_g', renderHeader: renderHeader, headerName: 'Required Seed Weight (g)', minWidth: 220, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => renderParentOnlyCell({ ...params, value: params.row?.required_seed_weight_g ?? params.row?.required_grams ?? '' }, classes),
        },
        {
            field: 'required_fruit_count', renderHeader: renderHeader, headerName: 'Required Fruit Count', minWidth: 170, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => renderParentOnlyCell({ ...params, value: params.row?.required_fruit_count ?? params.row?.required_fruit ?? '' }, classes),
        },
        {
            field: 'material_harvests', renderHeader: renderHeader, headerName: 'Material Harvests', minWidth: 140, flex: 1, cellClassName: `${classes.gridRowText} ${trailClasses.CenterAlignText}`,
            renderCell: (params: any) => (
                <Typography className={classes.gridRowText}>
                    {params.row?._rowType === 'child'
                        ? DASH_PLACEHOLDER
                        : (Array.isArray(params.row?.material_harvests)
                            ? params.row.material_harvests.length
                            : (params.row?.harvest_count ?? 0))}
                </Typography>
            ),
        },
    ];

    const baseColumns = allColumns.filter((c) => c.field !== '__expand__');
    const columnMap = new Map<string, GridColDef>();
    [...baseColumns, ...aliasColumns].forEach((column) => {
        if (column.field) {
            columnMap.set(column.field, column);
        }
    });

    const orderedColumns = requestedOrder
        .map((field) => columnMap.get(field))
        .filter((column): column is GridColDef => Boolean(column));

    return applyDashValueFormatter([allColumns[0], ...orderedColumns]);
}
